import { useCallback, useMemo, useState, type FormEvent, type JSX } from 'react';

import { getDefaultApiContext } from '../../api/client.js';
import { changePasswordApi } from '../../api/clients/auth.js';
import { useAuthStore } from '../../state/auth.js';

/** Mirrors the user-store floor (`password must be at least 8 characters`). */
const MIN_PASSWORD_LENGTH = 8;

interface FormState {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const EMPTY_FORM: FormState = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

/**
 * Self-service account panel — reached via the `#account` hash from the
 * `<UserMenu>` "Change password" item. Available to every authenticated user
 * (admin and non-admin alike). The form re-states the client-side rules the
 * server enforces (8-char floor, new ≠ current) so the common errors surface
 * before the round-trip; the server remains the authoritative gate.
 */
export function AccountPanel(props: { onClose: () => void }): JSX.Element {
  const me = useAuthStore((s) => s.user);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const ctx = useMemo(() => getDefaultApiContext(), []);

  const clientError = useMemo<string | null>(() => {
    if (form.newPassword.length > 0 && form.newPassword.length < MIN_PASSWORD_LENGTH) {
      return `New password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    }
    if (
      form.confirmPassword.length > 0 &&
      form.newPassword !== form.confirmPassword
    ) {
      return 'New password and confirmation do not match.';
    }
    if (
      form.newPassword.length > 0 &&
      form.newPassword === form.currentPassword
    ) {
      return 'New password must differ from the current password.';
    }
    return null;
  }, [form]);

  const canSubmit =
    !busy &&
    clientError === null &&
    form.currentPassword.length > 0 &&
    form.newPassword.length >= MIN_PASSWORD_LENGTH &&
    form.confirmPassword.length > 0;

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();
      if (!canSubmit) return;
      setBusy(true);
      setError(null);
      setSuccess(false);
      const res = await changePasswordApi(ctx, {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      setBusy(false);
      if (res.ok) {
        setForm(EMPTY_FORM);
        setSuccess(true);
      } else {
        setError(res.error.message);
      }
    },
    [canSubmit, ctx, form],
  );

  return (
    <div className="bg-cream-50 text-fg min-h-screen p-8" data-testid="account-panel">
      <header className="mb-8 flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-audi-red text-[11px] font-semibold uppercase tracking-[0.22em]">
            Account
          </span>
          <h1 className="text-fg text-3xl font-extrabold leading-[1.1] tracking-[-0.02em]">
            Change password
          </h1>
          {me !== null ? (
            <p className="text-taupe-700 mt-1 text-sm">
              Signed in as <span className="font-semibold">{me.username}</span>
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="border-taupe-500 text-fg hover:border-taupe-900 hover:bg-cream-100 rounded-none border px-4 py-2 text-xs font-semibold uppercase tracking-wide"
          onClick={props.onClose}
        >
          ← Back to TESSA
        </button>
      </header>

      <section className="border-taupe-300 bg-cream-50 max-w-md rounded-none border p-5">
        {success ? (
          <div
            role="status"
            data-testid="account-change-success"
            className="border-taupe-500 bg-cream-100 text-fg mb-4 rounded-none border px-3 py-2 text-sm"
          >
            Your password has been changed.
          </div>
        ) : null}

        {error !== null ? (
          <div
            role="alert"
            data-testid="account-change-error"
            className="border-audi-red bg-accent-red-bg text-audi-red mb-4 rounded-none border px-3 py-2 text-sm"
          >
            {error}
          </div>
        ) : null}

        <form
          onSubmit={(e) => {
            void onSubmit(e);
          }}
          className="flex flex-col gap-4"
        >
          <label className="block text-sm">
            <span className="text-taupe-700 mb-1 block text-[11px] font-semibold uppercase tracking-[0.12em]">
              Current password
            </span>
            <input
              type="password"
              autoComplete="current-password"
              value={form.currentPassword}
              onChange={(e) => {
                setForm((f) => ({ ...f, currentPassword: e.target.value }));
                setSuccess(false);
              }}
              disabled={busy}
              className="bg-cream-50 border-taupe-500 text-fg focus:border-audi-red focus:ring-audi-red w-full rounded-none border px-3 py-2 focus:outline-none focus:ring-1"
              required
            />
          </label>

          <label className="block text-sm">
            <span className="text-taupe-700 mb-1 block text-[11px] font-semibold uppercase tracking-[0.12em]">
              New password
            </span>
            <input
              type="password"
              autoComplete="new-password"
              value={form.newPassword}
              onChange={(e) => {
                setForm((f) => ({ ...f, newPassword: e.target.value }));
                setSuccess(false);
              }}
              disabled={busy}
              className="bg-cream-50 border-taupe-500 text-fg focus:border-audi-red focus:ring-audi-red w-full rounded-none border px-3 py-2 focus:outline-none focus:ring-1"
              required
              minLength={MIN_PASSWORD_LENGTH}
            />
          </label>

          <label className="block text-sm">
            <span className="text-taupe-700 mb-1 block text-[11px] font-semibold uppercase tracking-[0.12em]">
              Confirm new password
            </span>
            <input
              type="password"
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={(e) => {
                setForm((f) => ({ ...f, confirmPassword: e.target.value }));
                setSuccess(false);
              }}
              disabled={busy}
              className="bg-cream-50 border-taupe-500 text-fg focus:border-audi-red focus:ring-audi-red w-full rounded-none border px-3 py-2 focus:outline-none focus:ring-1"
              required
              minLength={MIN_PASSWORD_LENGTH}
            />
          </label>

          {clientError !== null ? (
            <p
              className="text-audi-red text-xs"
              data-testid="account-client-error"
              role="alert"
            >
              {clientError}
            </p>
          ) : null}

          <div>
            <button
              type="submit"
              disabled={!canSubmit}
              className="bg-taupe-900 hover:bg-audi-red rounded-none px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Changing…' : 'Change password'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default AccountPanel;
